import { PayboxTypeCarte } from 'types';
import { config } from 'config';
import crypto from 'crypto';
import { FormElement, IPayboxForm, PayboxRequest } from 'types';
import { verticals } from 'utils';

class Paybox {
  public async getFormData(
    transactionId: string,
    email: string,
    price: number,
    typeCarte: PayboxTypeCarte,
    vehicleCategory: string,
    action: 'create' | 'renew' | 'upsell' | 'update',
  ): Promise<IPayboxForm> {
    const request = this.buildRequest(transactionId, email, price, typeCarte, vehicleCategory, action);
    request.PBX_HMAC = this.computeHMAC(request);

    const elements: FormElement[] = [];
    for (const key of Object.keys(request)) {
      elements.push({
        name: key,
        value: request[key as keyof PayboxRequest]!,
      });
    }

    // Check if main server is alive, return fallback url if not alive
    const res = await fetch(`${config.URL_PAYBOX_MAIN}load.html`);
    const url = `${res.ok ? config.URL_PAYBOX_MAIN : config.URL_PAYBOX_FALLBACK}cgi/MYchoix_pagepaiement.cgi`;
    return { url, elements };
  }

  private buildRequest(
    transactionId: string,
    email: string,
    price: number,
    typeCarte: PayboxTypeCarte,
    vehicleCategory: string,
    action: 'create' | 'renew' | 'upsell' | 'update',
  ): PayboxRequest {
    return {
      PBX_SITE: config.PBX_LC_SITE,
      PBX_RANG: config.PBX_LC_RANK,
      PBX_IDENTIFIANT: config.PBX_LC_LOGIN,
      PBX_HMAC: config.PBX_HMAC_KEY,

      PBX_TOTAL: Number(price * 100).toString(), // en centime
      PBX_DEVISE: '978',

      PBX_CMD: transactionId,
      PBX_PORTEUR: email,
      PBX_RETOUR: config.PBX_RETOUR,

      PBX_TYPECARTE: this.getCardCode(typeCarte),
      PBX_TYPEPAIEMENT: 'CARTE',

      PBX_TIME: new Date().toISOString(),
      PBX_HASH: config.PBX_ALGORITHM,

      PBX_EFFECTUE: this.buildPbxEffectueURL(price, vehicleCategory, action),
      PBX_REFUSE: this.buildPbxRefuseURL(),
      PBX_ANNULE: this.buildPbxAnnuleURL(),
      PBX_REPONDRE_A: config.URL_INVOICES + config.PBX_REPONDRE_A,
    };
  }

  private getCardCode(typeCarte: PayboxTypeCarte) {
    switch (typeCarte) {
      case 'AURORE':
        return 'AURORE';
      case 'CB':
      case 'VISA':
      case 'EUROCARD_MASTERCARD':
      case 'E_CARD':
      default:
        return 'CB';
    }
  }

  private getElements(request: PayboxRequest) {
    const elements = [];
    for (const key of Object.keys(request)) {
      elements.push({
        name: key,
        value: (request as any)[key],
      });
    }
    return elements;
  }

  private computeHMAC(request: PayboxRequest) {
    if (request.PBX_HMAC) {
      const elements = this.getElements(request);
      const hmac = Buffer.from(request.PBX_HMAC, 'hex');
      const chain = elements
        .filter(e => e.name !== 'PBX_HMAC')
        .map(e => `${e.name}=${e.value}`)
        .join('&');

      return crypto.createHmac('sha512', hmac).update(chain).digest('hex').toUpperCase();
    }
  }

  private getCategoryFromVertical(vehicleCategory: string): string {
    const vertical = verticals[vehicleCategory];
    switch (vertical) {
      case 'AUTO':
        return 'Automobile';
      case 'MOTO':
        return '2roues';
      case 'UTILITAIRE':
        return 'Utilitaires';
      case 'CAMPING-CAR':
      case 'CARAVANE':
        return 'loisir';
      default:
        return 'OD';
    }
  }

  private getSSCatFromCategoryAndAction(category: string, action: string): number {
    switch (category) {
      case 'Automobile':
        return action === 'create' ? 1 : 8;
      case '2roues':
        return action === 'create' ? 2 : 9;
      case 'Utilitaires':
        return action === 'create' ? 3 : 10;
      case 'loisir':
        return action === 'create' ? 4 : 11;
      case 'OD':
      default:
        return action === 'create' ? 5 : 12;
    }
  }

  public buildPbxEffectueURL(
    price: number,
    vehicleCategory: string,
    action: 'create' | 'renew' | 'update' | 'upsell',
  ): string {
    const url = new URL(config.URL_LACENTRALE + config.PBX_EFFECTUE);
    const categorie = action === 'create' ? 1 : action === 'update' ? 2 : 3;
    const cat = this.getCategoryFromVertical(vehicleCategory);
    const sscat = this.getSSCatFromCategoryAndAction(cat, action);

    url.searchParams.append('mode', '2');
    url.searchParams.append('action', action);
    url.searchParams.append('categorie', categorie.toString());
    url.searchParams.append('cat', cat);
    url.searchParams.append('sscat', sscat.toString());
    url.searchParams.append('tarif', price.toString());
    url.searchParams.append('mttc', price.toString());
    url.searchParams.append('roimt', price.toString());

    return url.href;
  }

  private buildPbxRefuseURL(): string {
    const url = new URL(config.URL_LACENTRALE + config.PBX_REFUSE);
    url.searchParams.append('mode', '2');
    return url.href;
  }

  private buildPbxAnnuleURL(): string {
    const url = new URL(config.URL_LACENTRALE + config.PBX_ANNULE);
    url.searchParams.append('mode', '2');
    return url.href;
  }
}

export const PayboxService = new Paybox();
